import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InitForgotPassword, ResetPassword, VerifyCode } from '@auth/store/actions/auth.actions';
import { selectLoginError, selectLoginLoading, selectVerificationSentTo } from '@auth/store/selectors/login.selectors';
import { Store } from '@ngrx/store';
import { map, Observable, takeWhile, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ForgotPasswordComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  public readonly error$: Observable<Error | null>;
  public readonly loading$: Observable<boolean>;
  public readonly verificationSentTo$: Observable<string | null>;
  public timeRemaining$: Observable<number> | null = null;
  public email = new FormControl('', { nonNullable: true, validators: [Validators.required] });
  public code = new FormControl('', { nonNullable: true, validators: [Validators.required] });
  public verificationMethod = new FormControl<'email' | 'sms'>('email', {
    nonNullable: true,
    validators: [Validators.required],
  });
  public resetPasswordtexts = {
    title: 'Reset password',
    description: 'Enter your email address and we will send you a code or link to reset your password.',
    buttonText: 'Reset password',
    emailField: true,
    codeField: false,
    resendButton: false,
    passwordFields: false,
  };
  public codeVerificationTexts = {
    title: 'Verification code',
    description: 'Verification code has been sent to ',
    buttonText: 'Verify code',
    emailField: false,
    codeField: true,
    resendButton: true,
    passwordFields: false,
  };
  public linkVerificationTexts = {
    title: 'Verification link sent',
    description:
      'A password reset link has been sent to your email address. Please check your inbox and click on the link to reset your password.',
    buttonText: 'Back to login',
    emailField: false,
    codeField: false,
    resendButton: true,
    passwordFields: false,
  };
  public setNewPasswordTexts = {
    title: 'Password reset',
    description: '',
    buttonText: 'Save',
    emailField: false,
    codeField: false,
    resendButton: false,
    passwordFields: true,
  };
  public texts = this.resetPasswordtexts;
  public newPasswordFormGroup!: FormGroup;
  public showPasswordOne = false;
  public showPasswordTwo = false;
  public passFieldTypeOne: 'text' | 'password' = 'password';
  public passFieldTypeTwo: 'text' | 'password' = 'password';

  private emailValue: string = '';
  private resetPasswordToken: string = '';

  constructor() {
    this.error$ = this.store.select(selectLoginError);
    this.loading$ = this.store.select(selectLoginLoading);
    this.verificationSentTo$ = this.store.select(selectVerificationSentTo);

    // Get email queryparam from URL
    this.emailValue = this.route.snapshot.queryParamMap.get('email') || '';
    this.resetPasswordToken = this.route.snapshot.queryParamMap.get('resetPasswordToken') || '';

    // Set form fields and texts based on URL
    if (window.location.href.includes('forgot-password')) {
      this.texts = this.resetPasswordtexts;
    } else if (window.location.href.includes('code-verification')) {
      this.timeRemaining$ = timer(0, 1000).pipe(
        map((n) => (120 - n) * 1000),
        takeWhile((n) => n >= 0)
      );
      this.texts = this.codeVerificationTexts;
    } else if (window.location.href.includes('link-verification')) {
      this.timeRemaining$ = timer(0, 1000).pipe(
        map((n) => (120 - n) * 1000),
        takeWhile((n) => n >= 0)
      );
      this.texts = this.linkVerificationTexts;
    } else if (window.location.href.includes('reset-password')) {
      this.texts = this.setNewPasswordTexts;
      this.newPasswordFormGroup = this.formBuilder.group(
        {
          password: ['', { nonNullable: true, validators: [Validators.required] }],
          confirmPassword: ['', { nonNullable: true, validators: [Validators.required] }],
        },
        { validator: CustomValidators.MatchingPasswords }
      );
    }
  }

  public ngOnInit(): void {
    this.code.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: string) => {
      if (value.length === 6) {
        this.verifyCode();
      }
    });
  }

  public submit(): void {
    if (window.location.href.includes('forgot-password')) {
      this.initForgotPassword();
    } else if (window.location.href.includes('code-verification')) {
      this.verifyCode();
    } else if (window.location.href.includes('reset-password')) {
      this.resetPassword();
    } else if (window.location.href.includes('link-verification')) {
      this.router.navigate(['/']);
    }
  }

  public reSendCode(event: Event): void {
    event.preventDefault();
    if ((event.target as HTMLAnchorElement).classList.contains('disabled')) {
      return;
    }
    this.timeRemaining$ = timer(0, 1000).pipe(
      map((n) => (120 - n) * 1000),
      takeWhile((n) => n >= 0)
    );
    this.store.dispatch(InitForgotPassword({ email: this.emailValue, verification: this.verificationMethod.value }));
  }

  public toggleShowPassOne(): void {
    this.passFieldTypeOne = this.passFieldTypeOne === 'password' ? 'text' : 'password';
    this.showPasswordOne = !this.showPasswordOne;
  }

  public toggleShowPassTwo(): void {
    this.passFieldTypeTwo = this.passFieldTypeTwo === 'password' ? 'text' : 'password';
    this.showPasswordTwo = !this.showPasswordTwo;
  }

  private initForgotPassword(): void {
    if (this.email.invalid) {
      return;
    }
    this.emailValue = this.email.value;
    this.store.dispatch(InitForgotPassword({ email: this.emailValue, verification: this.verificationMethod.value }));
  }

  private verifyCode(): void {
    if (this.code.invalid) {
      return;
    }
    this.store.dispatch(VerifyCode({ email: this.emailValue, code: this.code.value }));
  }

  private resetPassword(): void {
    if (this.newPasswordFormGroup.invalid) {
      return;
    }
    this.store.dispatch(
      ResetPassword({
        password: this.newPasswordFormGroup.get('password')?.value,
        resetPasswordToken: this.resetPasswordToken,
      })
    );
  }
}

export class CustomValidators {
  static MatchingPasswords(control: AbstractControl) {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;
    const currentErrors = control.get('confirmPassword')?.errors;
    const confirmControl = control.get('confirmPassword');

    if (password !== confirmPassword && confirmPassword !== '') {
      confirmControl?.setErrors({ ...currentErrors, not_matching: true });
    } else {
      confirmControl?.setErrors(currentErrors as ValidationErrors);
    }
  }
}
